import React from 'react'
import { HelpCircle, Icon as IconType, Mail, PhoneOutgoing } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import shallow from 'zustand/shallow'

import { CobrandingLogo } from '@frame/ui-components/cobranding-logo'
import { Footer } from '@frame/ui-components/footer'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { A } from '@frame/ui-primitives/a'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { Card } from '@frame/ui-primitives/card'
import { H2 } from '@frame/ui-primitives/h2'
import { H4 } from '@frame/ui-primitives/h4'
import { P } from '@frame/ui-primitives/p'

import { InternalEventType } from '@frame/lib/api/events/types'
import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { TW_EMAIL, TW_PHONE } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { getTextForVerificationType, getVerifierDisplayName } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

type ExitButtonGroupProps = {
  handleExit?: () => void
}

type ExtendedExitButtonGroupProps = {
  consumerInitiated?: boolean
  hasBankTask?: boolean
} & ExitButtonGroupProps

function ConsumerInitiatedExitButtonGroup({ handleExit }: ExitButtonGroupProps) {
  const { events } = useRootContext()
  return (
    <>
      <Button data-cy={DataAttribute.BTN_CLOSE} intent="secondary" onClick={() => events.emit('close')}>
        I&apos;ll finish it later
      </Button>
      <Button data-cy={DataAttribute.BTN_EXIT} intent="secondary" border={false} onClick={handleExit} Icon={HelpCircle}>
        I have an issue
      </Button>
    </>
  )
}

function DefaultExitButtonGroup({ handleExit }: ExitButtonGroupProps) {
  return (
    <Button data-cy={DataAttribute.BTN_EXIT} intent="secondary" onClick={handleExit}>
      I&apos;ll do it later / I have an issue
    </Button>
  )
}

function BankExitButtonGroup() {
  return <></>
}

function ExitButtonGroup({ handleExit, consumerInitiated, hasBankTask }: ExtendedExitButtonGroupProps) {
  if (hasBankTask) {
    return <BankExitButtonGroup />
  }
  if (consumerInitiated) {
    return <ConsumerInitiatedExitButtonGroup handleExit={handleExit} />
  } else if (hasBankTask) {
    return <BankExitButtonGroup />
  } else {
    return <DefaultExitButtonGroup handleExit={handleExit} />
  }
}

export function ConfirmExit() {
  const navigate = useNavigate()

  const { handleEvent } = useEventHandler()
  const {
    maxEmployments,
    verifierName,
    verificationType,
    consumerInitiated,
    verifierType,
    tenantPropertyName,
    hasBankTask,
  } = useStore(
    (state) => ({
      maxEmployments: state.session?.max_employments ?? 1,
      verifierName: state.session?.originating_party_display_name,
      verificationType: state.session?.verification_type,
      consumerInitiated: state.session?.consumer_initiated,
      tenantPropertyName: state.session?.configuration?.cobranding?.tenant_property?.property_name,
      verifierType: state.session?.verifier_type,
      hasBankTask: state.session?.tasks.some((e) => e.strategy == TrueworkJsStrategy.BANK),
    }),
    shallow
  )

  const verificationTypeText = getTextForVerificationType(verificationType)
  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)

  const handleBackToVerification = () => {
    navigate(-1)
    handleEvent(InternalEventType.CANCELLED_EXIT)
  }

  const handleExit = () => {
    navigate(routes.feedback)
    handleEvent(InternalEventType.CONFIRMED_EXIT)
  }

  const getHelpText = (): string => {
    if (hasBankTask) {
      return 'Need some help?'
    }
    return 'Still need help?'
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        backButton={{ onClick: handleBackToVerification }}
        cancelButton={{ visible: false }}
      />
      <Body data-cy={DataAttribute.SCREEN_CONFIRM_EXIT} className="p-5" paddingTopOverride={0}>
        <CobrandingLogo />
        <H2 className="mb-3 mt-5">Your verification is incomplete!</H2>
        {maxEmployments === 1 && (
          <P>
            Exiting now will pause your <span className="font-medium">{verificationTypeText}</span> verification session
            and delay your application process with <span className="font-medium">{displayName}</span>.
          </P>
        )}
        <Card className="my-3 px-4 py-3" border>
          {!hasBankTask && (
            <div className="flex items-center border-b border-dashed pb-3">
              <P variant="small" className="mr-2 flex-1">
                Learn how to connect your payroll account in 60 seconds
              </P>
              <a href="https://www.truework.com/credentials-applicant-demo-video" target="_blank" rel="noreferrer">
                <Button
                  size="small"
                  intent="tertiary"
                  fullWidth={false}
                  onClick={() => {
                    handleEvent(InternalEventType.CLICKED_VIDEO_LINK)
                  }}
                >
                  Watch video
                </Button>
              </a>
            </div>
          )}
          <div className="mt-3 flex flex-col">
            <H4 className="mb-1">{getHelpText()}</H4>
            <ContactInfo Icon={Mail} contact={TW_EMAIL} href={`mailto:${TW_EMAIL}`} />
            <ContactInfo Icon={PhoneOutgoing} contact={TW_PHONE} href={`tel:${TW_PHONE}`} />
          </div>
        </Card>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button data-cy={DataAttribute.BTN_GO_BACK} applyCobranding onClick={handleBackToVerification}>
              Back to verification
            </Button>
          ),
          secondary: (
            <ExitButtonGroup handleExit={handleExit} consumerInitiated={consumerInitiated} hasBankTask={hasBankTask} />
          ),
        }}
      />
    </>
  )
}

function ContactInfo({ Icon, contact, href }: { Icon: IconType; contact: string; href: string }) {
  return (
    <div className="flex items-center">
      <Icon className="mr-3 inline-block text-cobranding" size={16} />
      <A href={href} target="_blank" rel="noreferrer">
        {contact}
      </A>
    </div>
  )
}
